import { NgFor, NgIf } from '@angular/common';
import { Component, forwardRef, Input, OnDestroy, OnInit } from '@angular/core';
import { ConnectionStatus } from '@capacitor/network';
import { Boondock, BoondockPoint, DEFAULT_IMAGE_URL, IPhoto, Listing, ListingPoint, Site } from '@curbnturf/entities';
import { PhotoDisplayModalComponent } from '@curbnturf/image-processing/src/lib/photo-display-modal/photo-display-modal.component';
import { SaveFavoriteComponent } from '@curbnturf/listing-edit/src/lib/save-favorite/save-favorite.component';
import { PhotoService } from '@curbnturf/photo';
import { StatusFacade } from '@curbnturf/status';
import { IonicModule, ModalController } from '@ionic/angular';
import { Subscription } from 'rxjs';

@Component({
  selector: 'curbnturf-photo-display',
  templateUrl: './photo-display.component.html',
  styleUrls: ['./photo-display.component.scss'],
  standalone: true,
  imports: [IonicModule, NgIf, NgFor, PhotoDisplayModalComponent, forwardRef(() => SaveFavoriteComponent)],
})
export class PhotoDisplayComponent implements OnInit, OnDestroy {
  @Input() photoElement: Listing | Site | Boondock;

  photoUri: string = DEFAULT_IMAGE_URL;

  photos: string[] = [];

  connectionStatus: ConnectionStatus = { connected: true, connectionType: 'wifi' };

  get point(): ListingPoint | undefined {
    if (this.photoElement instanceof Listing) {
      return ListingPoint.fromListing(this.photoElement);
    }

    if (this.photoElement instanceof Site && this.photoElement.listing) {
      return ListingPoint.fromListing(this.photoElement.listing);
    }
    return;
  }

  get boondockPoint(): BoondockPoint | undefined {
    if (this.photoElement instanceof Boondock) {
      return BoondockPoint.fromBoondock(this.photoElement);
    }
    return;
  }

  subscriptions: Subscription[] = [];

  constructor(
    private modalController: ModalController,
    private photoService: PhotoService,
    private statusFacade: StatusFacade,
  ) {}

  ngOnInit() {
    this.subscriptions.push(
      this.statusFacade.networkStatus$.subscribe(async (status) => {
        this.connectionStatus = status;
        this.photos = [];
        if (this.photoElement?.photos && this.photoElement?.photos.length > 0) {
          for (const photo of this.photoElement.photos) {
            this.photos.push(await this.getPhoto(photo));
          }
        }
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => {
      if (!sub.closed) {
        sub.unsubscribe();
      }
    });
  }

  async getPhoto(photo: IPhoto) {
    if (this.connectionStatus.connected) {
      return this.photoService.getPhotoOnline(photo);
    } else {
      return await this.photoService.getPhotoOffline(photo);
    }
  }

  async openModal() {
    const modal = await this.modalController.create({
      component: PhotoDisplayModalComponent,
      cssClass: 'photo-viewer-modal',
      componentProps: {
        photos: this.photoElement.photos,
      },
    });
    return await modal.present();
  }
}
